// Dérivations pures pour la liste des dossiers enrichie (registre).
// Aucune dépendance réseau : testable en vitest nu.
import { differenceInCalendarDays } from 'date-fns';
import { DOSSIER_TYPE_LABELS, type Dossier, type DossierMemberRole } from './dossierService';
import {
  type Contact,
  type DossierContactRole,
  getContactDisplayName,
} from '../contacts/contactService';

// ─── Types des embeds PostgREST (Task 2 : listDossiersEnriched) ─────────────

export type EmbeddedContact = Pick<
  Contact,
  'id' | 'kind' | 'first_name' | 'last_name' | 'company_name' | 'email'
>;

export interface EmbeddedContactLink {
  role: DossierContactRole;
  is_primary: boolean;
  contact: EmbeddedContact | null;
}

export interface EmbeddedMember {
  collaborator_id: string;
  role: DossierMemberRole;
}

export interface EmbeddedTask {
  id: string;
  content: string;
  deadline: string | null;
  due_date: string | null;
  is_completed: boolean;
  is_deleted: boolean;
}

export interface EmbeddedActivity {
  action: string;
  actor_id: string | null;
  details: unknown;
  created_at: string;
}

export interface DossierListItem extends Dossier {
  dossier_contacts: EmbeddedContactLink[];
  dossier_members: EmbeddedMember[];
  tasks: EmbeddedTask[];
  dossier_activity_log: EmbeddedActivity[];
  dossier_frise_steps?: { position: number; label: string }[];
}

// ─── Parties ─────────────────────────────────────────────────────────────────

/** Nom de la partie principale d'un rôle : is_primary d'abord, sinon la première. */
export function primaryPartyName(
  item: Pick<DossierListItem, 'dossier_contacts'>,
  role: DossierContactRole,
): string | null {
  const links = item.dossier_contacts.filter((l) => l.role === role && l.contact);
  if (!links.length) return null;
  const link = links.find((l) => l.is_primary) ?? links[0]!;
  return getContactDisplayName(link.contact as Contact);
}

// ─── Échéance ────────────────────────────────────────────────────────────────

export interface NextDeadline {
  date: string;
  label: string;
  kind: 'task' | 'hearing';
}

/**
 * Prochaine échéance = min(prochaine tâche non close (deadline ?? due_date),
 * next_hearing_at). Null si rien.
 */
export function nextDeadline(
  item: Pick<DossierListItem, 'tasks' | 'next_hearing_at'>,
): NextDeadline | null {
  const candidates: NextDeadline[] = [];

  for (const t of item.tasks) {
    if (t.is_completed || t.is_deleted) continue;
    const date = t.deadline ?? t.due_date;
    if (!date) continue;
    candidates.push({ date, label: t.content, kind: 'task' });
  }
  if (item.next_hearing_at) {
    candidates.push({ date: item.next_hearing_at, label: 'Audience', kind: 'hearing' });
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return candidates[0]!;
}

export type DeadlineUrgency = 'overdue' | 'urgent' | 'soon' | 'normal';

export function deadlineUrgency(dateIso: string, now: Date = new Date()): DeadlineUrgency {
  const days = differenceInCalendarDays(new Date(dateIso), now);
  if (days < 0) return 'overdue';
  if (days <= 7) return 'urgent';
  if (days <= 30) return 'soon';
  return 'normal';
}

/** Menu Échéance du registre. `week` = palier urgent (0–7 jours), pas `soon`. */
export type EcheanceFilter = 'overdue' | 'week' | 'none';

export function matchesEcheanceFilter(
  item: Pick<DossierListItem, 'tasks' | 'next_hearing_at'>,
  filter: EcheanceFilter,
  now: Date = new Date(),
): boolean {
  const next = nextDeadline(item);
  if (filter === 'none') return next === null;
  if (!next) return false;
  const urgency = deadlineUrgency(next.date, now);
  if (filter === 'overdue') return urgency === 'overdue';
  return urgency === 'urgent';
}

// ─── Activité ────────────────────────────────────────────────────────────────

/** Libellés FR des actions de dossier_activity_log (réutilisé par l'Aperçu). */
export const ACTIVITY_LABELS: Record<string, string> = {
  'file.upload': 'Pièce ajoutée',
  'file.delete': 'Pièce supprimée',
  'file.move': 'Pièce déplacée',
  'file.replace': 'Pièce remplacée',
  'file.visibility_change': 'Visibilité modifiée',
  'file_imported': 'Pièce importée',
  'folder.create': 'Sous-dossier créé',
  'folder.rename': 'Sous-dossier renommé',
  'folder.delete': 'Sous-dossier supprimé',
  'folder.template_apply': 'Arborescence appliquée',
  email_grafted: 'Mail greffé',
  email_grafted_read: 'Mail reçu greffé',
  email_archived_sent: 'Mail envoyé greffé',
  email_graft_removed: 'Greffe retiré',
  email_moved_in: 'Mail déplacé ici',
  email_moved_out: 'Mail déplacé vers un autre dossier',
  document_generated: 'Document généré',
  'task.file_link': 'Pièce liée à une tâche',
  'task.file_unlink': 'Pièce détachée d’une tâche',
  'fee_agreement.ai_created': 'Convention d’honoraires générée',
  'client.file_download': 'Téléchargement par le client',
  dossier_deleted: 'Dossier supprimé',
  signature_circuit_created: 'Parapheur lancé',
  signature_step_signed: 'Visa apposé',
  signature_step_rejected: 'Visa refusé',
  signature_circuit_cancelled: 'Parapheur annulé',
  signature_circuit_finalized: 'Parapheur finalisé',
  signature_circuit_resubmitted: 'Parapheur renvoyé',
  lead_counsel_changed: 'Responsable modifié',
};

export function activityLabel(action: string): string {
  return ACTIVITY_LABELS[action] ?? action;
}

export interface LastActivity {
  label: string;
  actorId: string | null;
  at: string;
}

export function lastActivity(
  item: Pick<DossierListItem, 'dossier_activity_log'>,
): LastActivity | null {
  const entry = item.dossier_activity_log[0];
  if (!entry) return null;
  return {
    label: activityLabel(entry.action),
    actorId: entry.actor_id,
    at: entry.created_at,
  };
}

// ─── Tri & recherche ─────────────────────────────────────────────────────────

/**
 * Ordre du registre : actifs avec échéance (croissante), puis actifs sans
 * échéance (ouverture décroissante), puis clos/archivés (ouverture décroissante).
 */
export function compareByDeadline(a: DossierListItem, b: DossierListItem): number {
  const rank = (d: DossierListItem) =>
    d.closed_at || d.archived_at ? 2 : nextDeadline(d) ? 0 : 1;
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return ra - rb;
  if (ra === 0) {
    return (
      new Date(nextDeadline(a)!.date).getTime() - new Date(nextDeadline(b)!.date).getTime()
    );
  }
  return new Date(b.opened_at).getTime() - new Date(a.opened_at).getTime();
}

export type RegistreSortKey =
  | 'affaire'
  | 'client'
  | 'adv'
  | 'equipe'
  | 'etape'
  | 'echeance'
  | 'audience'
  | 'prescription'
  | 'matiere'
  | 'juridiction'
  | 'rg'
  | 'ouverture'
  | 'aj'
  | 'activite';

export type SortDir = 'asc' | 'desc';

export interface RegistreSortContext {
  leadName: (leadCounselId: string | null) => string | null;
  stepLabel: (item: DossierListItem) => string | null;
}

const DATE_SORT_KEYS = new Set<RegistreSortKey>([
  'echeance',
  'activite',
  'audience',
  'prescription',
  'ouverture',
]);

export function registreSortIsDate(key: RegistreSortKey): boolean {
  return DATE_SORT_KEYS.has(key);
}

function registreSortRaw(
  item: DossierListItem,
  key: RegistreSortKey,
  ctx: RegistreSortContext,
): string {
  switch (key) {
    case 'affaire':
      return item.title ?? '';
    case 'client':
      return primaryPartyName(item, 'client') ?? '';
    case 'adv':
      return primaryPartyName(item, 'opposing_party') ?? '';
    case 'equipe':
      return ctx.leadName(item.lead_counsel_id) ?? '';
    case 'etape':
      return ctx.stepLabel(item) ?? '';
    case 'echeance':
      return nextDeadline(item)?.date ?? '';
    case 'audience':
      return item.next_hearing_at ?? '';
    case 'prescription':
      return item.prescription_date ?? '';
    case 'matiere':
      return item.type_affaire ? DOSSIER_TYPE_LABELS[item.type_affaire] : '';
    case 'juridiction':
      return item.juridiction ?? '';
    case 'rg':
      return item.rg_number ?? '';
    case 'ouverture':
      return item.opened_at ?? '';
    case 'aj':
      return item.aj_flag ? 'oui' : '';
    case 'activite':
      return lastActivity(item)?.at ?? '';
  }
}

function instant(iso: string): number | null {
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * Tri d’en-tête du registre. Les vides restent en dernier dans les deux sens.
 * À valeur égale, l’intitulé départage (toujours croissant).
 */
export function compareRegistre(
  a: DossierListItem,
  b: DossierListItem,
  key: RegistreSortKey,
  dir: SortDir,
  ctx: RegistreSortContext,
): number {
  const av = registreSortRaw(a, key, ctx).trim();
  const bv = registreSortRaw(b, key, ctx).trim();
  if (!av && bv) return 1;
  if (av && !bv) return -1;
  if (av && bv && av !== bv) {
    const cmp = DATE_SORT_KEYS.has(key)
      ? (instant(av) ?? 0) - (instant(bv) ?? 0)
      : av.localeCompare(bv, 'fr', { sensitivity: 'base' });
    if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
  }
  return a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' });
}

/** Recherche client-side : numéro, intitulé, noms des parties. */
export function matchesSearch(item: DossierListItem, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  if (item.number.toLowerCase().includes(needle)) return true;
  if (item.title.toLowerCase().includes(needle)) return true;
  if (item.description?.toLowerCase().includes(needle)) return true;
  if (item.juridiction?.toLowerCase().includes(needle)) return true;
  if (item.rg_number?.toLowerCase().includes(needle)) return true;
  return item.dossier_contacts.some(
    (l) => l.contact && getContactDisplayName(l.contact as Contact).toLowerCase().includes(needle),
  );
}
